import { useState } from "react";
import { Check, BadgePercent, Send, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useSite } from "../../context/SiteContext";
import { fmtEUR, api, apiError, API } from "../../lib/api";
import { Reveal, SectionHeader } from "./Sections";
import { RichText } from "../RichText";
import { Stars, TestimonialForm } from "../TestimonialForm";
import { StoreButtons } from "../StoreButtons";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

export function Pricing() {
    const { data } = useSite();
    const sec = data?.sections?.pricing;
    const offers = data?.offers || [];
    if (!sec || sec.visible === false) return null;
    return (
        <section id="tarifs" className="bg-pearl py-24 sm:py-28 scroll-mt-20" data-testid="pricing-section">
            <div className="container-bc">
                <SectionHeader eyebrow={sec.eyebrow || "Tarifs"} title={sec.title} subtitle={sec.subtitle} />
                {offers.length === 0 ? (
                    <p className="text-ink-soft" data-testid="pricing-empty">Les offres seront publiées prochainement.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {offers.map((o) => (
                            <Reveal key={o.offer_id}>
                                <article className="relative h-full card-light p-8 flex flex-col" data-testid={`offer-card-${o.offer_id}`}>
                                    {o.promo?.live && (
                                        <span className="absolute -top-3 right-6 inline-flex items-center gap-1 bg-gold text-white text-xs font-bold px-3 py-1.5 rounded-full" data-testid="promo-badge">
                                            <BadgePercent size={14} /> -{o.promo.percent || ""}%
                                        </span>
                                    )}
                                    <h3 className="font-heading font-bold text-ink text-xl">{o.name}</h3>
                                    <p className="text-ink-soft text-sm mt-2 leading-relaxed">{o.description}</p>
                                    <div className="mt-6 flex items-end gap-3">
                                        {o.promo?.live && o.promo.promo_price_cents != null ? (
                                            <>
                                                <span className="font-heading font-extrabold text-4xl text-forest" data-testid="promo-price">
                                                    {fmtEUR(o.promo.promo_price_cents, o.currency)}
                                                </span>
                                                <span className="text-ink-soft line-through text-lg mb-1" data-testid="official-price">
                                                    {fmtEUR(o.price_cents, o.currency)}
                                                </span>
                                            </>
                                        ) : (
                                            <span className="font-heading font-extrabold text-4xl text-ink" data-testid="official-price">
                                                {fmtEUR(o.price_cents, o.currency)}
                                            </span>
                                        )}
                                        <span className="text-ink-soft text-sm mb-1.5">TTC / {o.periodicity}</span>
                                    </div>
                                    <ul className="mt-6 space-y-2.5 flex-1">
                                        {(o.features || []).map((f, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-ink">
                                                <Check size={16} className="text-gold-dark mt-0.5 shrink-0" /> {f}
                                            </li>
                                        ))}
                                    </ul>
                                    {o.promo?.live && o.promo.conditions && (
                                        <p className="mt-5 text-xs text-ink-soft leading-relaxed border-t border-border pt-4">{o.promo.conditions}</p>
                                    )}
                                    <p className="mt-3 text-xs text-ink-soft">
                                        Achat et gestion de l'abonnement exclusivement via Google Play ou l'App Store.
                                    </p>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

export function FeaturedShop() {
    const { data } = useSite();
    const sec = data?.sections?.home_shop;
    const p = data?.featured_product;
    if (!sec || sec.visible === false || !p) return null;
    return (
        <section className="bg-ivory py-24 sm:py-28" data-testid="featured-shop-section">
            <div className="container-bc">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <Reveal className="md:col-span-6">
                        <div className="relative">
                            <div className="absolute -inset-2 rounded-3xl border border-gold/50 translate-x-3 translate-y-3" aria-hidden="true" />
                            {p.image ? (
                                <img
                                    src={`${API}/shop-media/${p.product_id}/${p.image.id}`}
                                    alt={p.image.title || p.name}
                                    loading="lazy"
                                    className="relative rounded-3xl object-cover w-full aspect-[4/3] gold-frame"
                                    data-testid="featured-product-image"
                                />
                            ) : (
                                <div className="relative rounded-3xl bg-pearl aspect-[4/3] flex items-center justify-center text-ink-soft">Visuel à venir</div>
                            )}
                        </div>
                    </Reveal>
                    <Reveal className="md:col-span-6" delay={0.1}>
                        <p className="eyebrow mb-3">{sec.eyebrow || "La Boutique"}</p>
                        <h2 className="font-heading font-extrabold text-ink text-2xl sm:text-3xl lg:text-4xl tracking-tight text-balance">{sec.title}</h2>
                        <p className="text-ink-soft mt-4 leading-relaxed">{sec.subtitle}</p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <Link to={`/boutique/${p.slug}`} className="inline-flex items-center gap-2 bg-forest text-ivory px-7 py-3 rounded-full font-semibold hover:bg-forest-surface transition-colors" data-testid="featured-product-cta">
                                Découvrir la balance <ArrowRight size={16} />
                            </Link>
                            <Link to="/boutique" className="inline-flex items-center gap-2 border border-forest/30 text-forest px-7 py-3 rounded-full font-semibold hover:bg-pearl transition-colors" data-testid="featured-shop-cta">
                                {sec.cta_label || "Voir la boutique"}
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

export function Testimonials() {
    const { data } = useSite();
    const sec = data?.sections?.testimonials;
    const [formOpen, setFormOpen] = useState(false);
    const testimonials = data?.testimonials || [];
    if (!sec || sec.visible === false) return null;
    return (
        <section className="bg-ivory py-24 sm:py-28" data-testid="testimonials-section">
            <div className="container-bc">
                <SectionHeader eyebrow={sec.eyebrow || "Témoignages"} title={sec.title} subtitle={sec.subtitle} />
                {testimonials.length === 0 ? (
                    <Reveal>
                        <div className="rounded-3xl border-2 border-dashed border-gold/50 bg-pearl p-10 text-center max-w-2xl" data-testid="testimonials-empty">
                            <p className="font-heading font-bold text-ink text-xl">Aucun témoignage publié pour le moment</p>
                            <p className="text-ink-soft mt-2 text-sm">
                                Les avis publiés ici sont de vrais témoignages, relus par notre équipe avant publication. Soyez le premier à partager votre expérience.
                            </p>
                        </div>
                    </Reveal>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {testimonials.map((t) => (
                            <Reveal key={t.testimonial_id}>
                                <figure className="h-full card-light p-6" data-testid={`testimonial-${t.testimonial_id}`}>
                                    {t.rating != null && <Stars value={t.rating} />}
                                    <blockquote className="mt-3 text-ink text-sm leading-relaxed">« {t.text} »</blockquote>
                                    <figcaption className="mt-4 text-sm font-semibold text-ink-soft">{t.name}</figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                )}
                <Reveal className="mt-10">
                    <Button onClick={() => setFormOpen(true)} className="bg-forest text-ivory hover:bg-forest-surface font-semibold" data-testid="open-testimonial-form-button">
                        {sec.cta_label || "Partager mon expérience"}
                    </Button>
                </Reveal>
                <TestimonialForm open={formOpen} onClose={() => setFormOpen(false)} />
            </div>
        </section>
    );
}

export function Faq() {
    const { data } = useSite();
    const sec = data?.sections?.faq;
    const faq = data?.faq || [];
    if (!sec || sec.visible === false || faq.length === 0) return null;
    return (
        <section id="faq" className="bg-pearl py-24 sm:py-28 scroll-mt-20" data-testid="faq-section">
            <div className="container-bc max-w-4xl">
                <SectionHeader eyebrow={sec.eyebrow || "FAQ"} title={sec.title} subtitle={sec.subtitle} />
                <Accordion type="single" collapsible className="space-y-3">
                    {faq.map((f) => (
                        <AccordionItem key={f.faq_id} value={f.faq_id} className="card-light px-6 border" data-testid={`faq-item-${f.faq_id}`}>
                            <AccordionTrigger className="font-heading font-semibold text-ink text-left">{f.question}</AccordionTrigger>
                            <AccordionContent className="text-ink-soft leading-relaxed">
                                {f.answer_html ? <RichText html={f.answer_html} testid={`faq-answer-${f.faq_id}`} /> : f.answer}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
}

export function DownloadCTA() {
    const { data } = useSite();
    const sec = data?.sections?.download;
    if (!sec || sec.visible === false) return null;
    return (
        <section id="telecharger" className="bg-ivory py-24 sm:py-32 scroll-mt-20" data-testid="download-section">
            <div className="container-bc">
                <Reveal>
                    <div className="relative rounded-[2.5rem] bg-forest-deep grain overflow-hidden px-8 py-16 sm:p-20 text-center gold-frame">
                        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gold/15 blur-3xl" aria-hidden="true" />
                        <h2 className="relative font-heading font-extrabold text-ivory text-3xl sm:text-4xl lg:text-5xl tracking-tight text-balance max-w-3xl mx-auto">
                            {sec.title}
                        </h2>
                        <p className="relative text-ivory/70 mt-5 max-w-xl mx-auto text-base sm:text-lg">{sec.subtitle}</p>
                        <div className="relative mt-10 flex justify-center">
                            <StoreButtons dark size="lg" />
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

export function ContactSection() {
    const { data } = useSite();
    const sec = data?.sections?.contact_intro || {};
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", company: "" });
    const [state, setState] = useState({ loading: false, error: "", done: "" });

    const submit = async (e) => {
        e.preventDefault();
        setState({ loading: true, error: "", done: "" });
        try {
            const { data } = await api.post("/public/contact", form);
            setState({ loading: false, error: "", done: data.detail });
            setForm({ name: "", email: "", subject: "", message: "", company: "" });
        } catch (err) {
            setState({ loading: false, error: apiError(err), done: "" });
        }
    };

    return (
        <section id="contact" className="bg-pearl py-24 sm:py-28 scroll-mt-20" data-testid="contact-section">
            <div className="container-bc grid grid-cols-1 lg:grid-cols-2 gap-12">
                <SectionHeader
                    eyebrow={sec.eyebrow || "Contact"}
                    title={sec.title || "Une question ? Écrivez-nous."}
                    subtitle={sec.subtitle || "Notre équipe lit chaque message. Nous vous répondrons à l'adresse e-mail indiquée."}
                />
                <Reveal>
                    {state.done ? (
                        <p className="card-light p-6 text-ink" data-testid="contact-success">{state.done}</p>
                    ) : (
                        <form onSubmit={submit} className="card-light p-8 space-y-4" data-testid="contact-form">
                            <input type="text" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="c-name">Nom</Label>
                                    <Input id="c-name" data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                                </div>
                                <div>
                                    <Label htmlFor="c-email">E-mail</Label>
                                    <Input id="c-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="c-subject">Sujet</Label>
                                <Input id="c-subject" data-testid="contact-subject-input" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                            </div>
                            <div>
                                <Label htmlFor="c-message">Message</Label>
                                <Textarea id="c-message" rows={5} data-testid="contact-message-input" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                            </div>
                            {state.error && <p className="text-sm text-destructive" data-testid="contact-error">{state.error}</p>}
                            <Button type="submit" disabled={state.loading} className="bg-forest text-ivory hover:bg-forest-surface gap-2" data-testid="contact-submit-button">
                                <Send size={15} /> {state.loading ? "Envoi…" : "Envoyer le message"}
                            </Button>
                        </form>
                    )}
                </Reveal>
            </div>
        </section>
    );
}
